import TiptapTableRow from "@tiptap/extension-table-row";

import { findTable, getSelectedRowIndex, setBoldMarkOnFragment } from "./utils";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    tableRow: {
      /** Toggle the grip-selected (or cursor) row between header (bold + shaded) and regular cells. */
      toggleHeaderRowShading: () => ReturnType;
    };
  }
}

export const TableRow = TiptapTableRow.extend({
  allowGapCursor: false,
  content: "(tableCell | tableHeader)*",

  addCommands() {
    return {
      toggleHeaderRowShading:
        () =>
        ({ state, dispatch }) => {
          const table = findTable(state.selection);
          if (!table) return false;

          const rowIndex = getSelectedRowIndex(state.selection);
          // Only the table's first row can become a header — pdf-report-core only recognizes a
          // leading run of tableHeader rows as a repeating PDF header, so a header set on any
          // other row would look right in the editor but silently not repeat across page breaks.
          if (rowIndex !== 0) return false;

          const { schema } = state;
          const headerType = schema.nodes.tableHeader;
          const cellType = schema.nodes.tableCell;
          const row = table.node.child(rowIndex);

          const isCurrentlyHeader =
            row.childCount > 0 &&
            (() => {
              let allHeader = true;
              row.forEach((cell) => {
                if (cell.type !== headerType) allHeader = false;
              });
              return allHeader;
            })();

          const targetType = isCurrentlyHeader ? cellType : headerType;

          if (dispatch) {
            const newCells: any[] = [];
            row.forEach((cell) => {
              const attrs: Record<string, unknown> = {
                colspan: cell.attrs.colspan,
                rowspan: cell.attrs.rowspan,
                colwidth: cell.attrs.colwidth,
              };
              // Becoming a regular cell: explicitly clear the shading. Becoming a
              // header: omit backgroundColor so it picks up the schema default.
              if (isCurrentlyHeader) attrs.backgroundColor = null;

              // pdf-report-core reads bold off each paragraph's own text marks, not off the
              // tableHeader/tableCell node type, so the toggle has to (un)bold the text itself.
              // Users can still manually unbold specific text afterwards via the normal bold toggle.
              const content = setBoldMarkOnFragment(cell.content, schema, !isCurrentlyHeader);

              newCells.push(targetType.create(attrs, content));
            });
            const newRow = row.type.create(row.attrs, newCells);

            let rowPos = table.start;
            for (let i = 0; i < rowIndex; i += 1) {
              rowPos += table.node.child(i).nodeSize;
            }

            dispatch(state.tr.replaceWith(rowPos, rowPos + row.nodeSize, newRow));
          }

          return true;
        },
    };
  },
});

export default TableRow;
